import { Box } from '@mui/material';
import NotificationFilter from './notification-filter';
import NotificationCard from './notification-card';
import Loader from '../../common/loader';
import Layout from '../../common/layout';
import { useSelector } from 'react-redux';
import { RootState } from '../../common/utils/root-state';
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { GET_MEMBERSS, GET_NOTIFICATIONS } from '../gql';
import { client } from '../../..';
import { INotification, INotificationData } from '../type';
import NoRecords from '../../common/application/no-records';
import { markNotifications } from '../../common/utils/mark-notification-service';

function Notifications(): JSX.Element {
  const [resource, setResource] = useState<{
    displayName: string;
    id: string;
  } | null>(null);
  const [project, setProject] = useState<{
    projectName: string;
    id: string;
  } | null>(null);
  const [projectManager, setProjectManager] = useState<{
    displayName: string;
    id: string;
  } | null>(null);
  const [flagged, setFlagged] = useState<boolean>(false);
  const memberId = useSelector(
    (state: RootState) => state.profileData.profileData?.memberId
  );

  const [notifications, setNotifications] = useState<INotification[]>([]);
  const [skipCount, setSkipCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const prevFilters = useRef({
    resource,
    project,
    projectManager,
    flagged
  });

  const prevSkip = useRef({
    skipCount
  });

  const observerRefs = useRef<HTMLDivElement[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const { refetch, data: notificationsData } = useQuery(GET_NOTIFICATIONS, {
    variables: {
      memberId: memberId ?? '',
      isFlagged: flagged ? true : undefined,
      projectId: project?.id ?? undefined,
      resourceId: resource?.id ?? undefined,
      deliveryResponsibleId: projectManager?.id ?? undefined,
      skip: 0
    }
  });

  let unreadNotificationIds: string[] = [];

  async function loadNotifications(
    data: INotificationData
  ): Promise<INotification[]> {
    if (!data?.getNotifications) {
      return [];
    }

    const fetchNotifications = data.getNotifications;

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    const processMetadata = async (metadata: any, idField: string) => {
      if (metadata[idField] && Array.isArray(metadata[idField])) {
        try {
          const memberResult = await client.query({
            query: GET_MEMBERSS,
            variables: { ids: metadata[idField] }
          });
          // eslint-disable-next-line no-param-reassign
          metadata[idField] = memberResult.data.getMembers.data ?? [];
        } catch (error) {
          console.error(`Error fetching ${idField}:`, error);
        }
      }
    };

    const modifiedNotifications = await Promise.all(
      fetchNotifications.map(async (notification) => {
        const notificationCopy = JSON.parse(JSON.stringify(notification));

        const processFields = [
          { path: 'projectChange', idField: 'members' },
          { path: 'approvalRequest', idField: 'resourceIds' }
        ];

        for (const { path, idField } of processFields) {
          if (notificationCopy[path]?.metadata) {
            const metadata = JSON.parse(notificationCopy[path].metadata);
            notificationCopy[path].metadata = metadata;
            await processMetadata(metadata, idField);
          }
        }

        return notificationCopy;
      })
    );

    return modifiedNotifications;
  }

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (notificationsData) {
        const modifiedNotifications = await loadNotifications(
          notificationsData
        );

        const filtersChanged =
          resource?.id !== prevFilters.current.resource?.id ||
          project?.id !== prevFilters.current.project?.id ||
          projectManager?.id !== prevFilters.current.projectManager?.id ||
          flagged !== prevFilters.current.flagged;

        if (filtersChanged) {
          setNotifications(modifiedNotifications);
          prevFilters.current = { resource, project, projectManager, flagged };
          setIsLoading((state) => !state);
        } else if (skipCount !== prevSkip.current.skipCount) {
          setNotifications((prevNotifications) => [
            ...prevNotifications,
            ...modifiedNotifications
          ]);
        } else {
          setNotifications(modifiedNotifications);
          setIsLoading(true);
          await refetch();
          setIsLoading(false);
        }
      }
    };
    void fetchData();
  }, [notificationsData, resource, project, projectManager, flagged]);

  useEffect(() => {
    setIsLoading((state) => !state);
  }, [resource, project, projectManager, flagged]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const notificationId = entry.target.getAttribute('data-id');
            if (notificationId) {
              handleNotificationInViewport(notificationId);
            }
          }
        });
      },
      {
        root: containerRef.current,
        threshold: 1
      }
    );

    observerRefs.current.forEach((ref) => {
      if (ref) {
        observer.observe(ref);
      }
    });

    return () => {
      observerRefs.current.forEach((ref) => {
        if (ref) {
          observer.unobserve(ref);
        }
      });
    };
  }, [notifications]);

  const handleMarkAsRead = async (notificationIds: string[]): Promise<void> => {
    const deliveredAt = new Date().toISOString();
    await markNotifications(notificationIds, deliveredAt);
    setNotifications((prev) =>
      prev.map((notification) =>
        notificationIds.includes(notification.id)
          ? { ...notification, deliveredAt }
          : notification
      )
    );
  };

  const handleNotificationInViewport = (() => {
    let timeout: ReturnType<typeof setTimeout>;
    return (notificationId: string): void => {
      const index = notifications.findIndex(
        (notif) => notif.id === notificationId
      );
      if (index !== -1 && !notifications[index].deliveredAt) {
        if (!unreadNotificationIds.includes(notificationId)) {
          unreadNotificationIds.push(notificationId);

          clearTimeout(timeout);

          timeout = setTimeout(() => {
            void handleMarkAsRead(unreadNotificationIds);
            unreadNotificationIds = [];
          }, 6000);
        }
      }
    };
  })();

  const handleScroll = (event: React.UIEvent<HTMLElement>): void => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 2) {
      const currentLength = notifications.length;
      void handleLoadMore(currentLength);
      setSkipCount(currentLength);
    }
  };

  const handleLoadMore = async (skip: number): Promise<void> => {
    await refetch({
      skip
    });
  };

  const filteredNotification = notifications.filter(
    (notification) =>
      !(
        notification.type === 'INDIVIDUAL_PROJECT_CHANGE' &&
        notification.actionUserId === memberId
      )
  );
  return (
    <Layout
      mainHeading="Notifications"
      subHeading="User Management > Notifications"
    >
      <NotificationFilter
        setResourceData={(
          value: {
            displayName: string;
            id: string;
          } | null
        ) => setResource(value)}
        setProjectData={(
          value: {
            projectName: string;
            id: string;
          } | null
        ) => setProject(value)}
        setProjectManagerData={(
          value: {
            displayName: string;
            id: string;
          } | null
        ) => setProjectManager(value)}
        setFlaggedData={(value: boolean) => setFlagged(value)}
      />
      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          overflowY: 'auto',
          height: 'calc(100vh - 150px)',
          '&::-webkit-scrollbar': {
            width: '0.4em'
          },
          '&::-webkit-scrollbar-track': {
            boxShadow: 'inset 0 0 6px rgba(0, 0, 0, 0.2)',
            borderRadius: '10px'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            borderRadius: '10px'
          },
          paddingRight: '5px'
        }}
        onScroll={handleScroll}
      >
        {isLoading && notifications.length === 0 ? (
          <Loader />
        ) : notifications.length !== 0 ? (
          <>
            {filteredNotification.map((notification, index) => {
              const resources = notification.projectChange?.metadata
                ? notification.projectChange.metadata.members
                : notification.approvalRequest?.metadata
                ? notification.approvalRequest.metadata.resourceIds
                : [];

              let flaged = null;
              if (notification.type === 'PROJECT_CHANGE') {
                flaged = notification.projectChange
                  ? notification.isFlagged
                    ? notification.isFlagged
                    : false
                  : null;
              }

              const message = notification.projectChange
                ? notification.projectChange.description
                : notification.description;
              return (
                <Box
                  ref={(el) => {
                    if (el instanceof HTMLDivElement) {
                      observerRefs.current[index] = el;
                    }
                  }}
                  data-id={notification.id}
                  key={notification.id}
                >
                  <NotificationCard
                    id={notification.id}
                    message={message}
                    date={new Date(notification.createdAt)}
                    flaged={flaged}
                    resouces={resources}
                    deliveredAt={notification.deliveredAt}
                    skillName={notification.approvalRequest?.metadata.skillName}
                  />
                </Box>
              );
            })}
          </>
        ) : (
          <NoRecords message={'No Notifications Available'} />
        )}
      </Box>
    </Layout>
  );
}

export default Notifications;
